<script setup lang="ts">
import { reactive, computed, ref, watch, onMounted } from 'vue'
import { generateCronExpression } from '~/composables/useCronParser'
import { Type } from '~/types/action-types'
import { cleanText } from '~/Common/StringOperations'
import { useClientGroupsStore } from '~/stores/client-groups'
import { useClientsStore } from '~/stores/clients'
import { useScheduledActionsStore } from '~/stores/scheduled-actions'
import { useI18n } from 'vue-i18n'
import { useToast } from 'primevue/usetoast';

const { t, locale } = useI18n()
const toast = useToast()
const scheduledActionsStore = useScheduledActionsStore()
const clientsStore = useClientsStore()

const props = defineProps<{
  isOpen: boolean
  groupId?: number | null
  clientId?: number | null
  clientIds?: number[]
  actionType?: Type
  description?: string
  extraData?: Record<string, any> | string
  actionId?: number
  startAt?: string
  endAt?: string
  cronExpression?: string
  timezone?: string
}>()

const emit = defineEmits(['update:isOpen', 'save', 'saved'])

const visible = ref(props.isOpen)
watch(() => props.isOpen, v => (visible.value = v))
watch(visible, v => { if (v !== props.isOpen) emit('update:isOpen', v) })

// helper: safely parse extraData if it's a JSON string, otherwise return a shallow copy/object
function parseExtraData(ed: any): Record<string, any> {
  // ed for extraData
  if (!ed) return {}
  if (typeof ed === 'string') {
    try {
      const parsed = JSON.parse(ed)
      return typeof parsed === 'object' && parsed !== null ? parsed : {}
    } catch {
      return {}
    }
  }
  if (typeof ed === 'object') {
    return { ...ed }
  }
  return {}
}

const rawTimezones = ref((Intl as any).supportedValuesOf('timeZone'))
const userTimezone = ref(Intl.DateTimeFormat().resolvedOptions().timeZone)

const timezones = computed(() => {
  const date = new Date(2023, 0, 1, 12, 0, 0)
  return rawTimezones.value.map((tz: string) => {
    const parts = new Intl.DateTimeFormat(locale.value, {
      timeZone: tz,
      timeZoneName: 'long'
    })
      .format(date)
      .split(', ')
    const name = parts[parts.length - 1] || tz
    return { value: tz, label: `${name} (${tz})` }
  })
})

const groupsStore = useClientGroupsStore()
onMounted(() => groupsStore.getGroups())

const availableGroups = computed(() =>
  groupsStore.groups.map(g => ({ label: g.name, value: g.id }))
)
const isLoadingGroups = computed(() => groupsStore.loading)

function close() {
  emit('update:isOpen', false)
}

type Mode = 'once' | 'interval' | 'cron'
type IntervalUnit = 'Minute' | 'Hour' | 'Day' | 'Week' | 'Month' | 'Custom'
type Ends = 'never' | 'on'

const state = reactive({
  enabled: true,
  mode: 'interval' as Mode,
  timezone: props.timezone ?? userTimezone.value,
  actionType: props.actionType ?? Type.reboot,
  description: props.description ?? '',
  extraData: parseExtraData(props.extraData),
  delegateClientId: undefined as number | undefined,
  onceDateTime: new Date(),
  every: 1,
  unit: 'Week' as IntervalUnit,
  startDateTime: new Date(),
  ends: 'never' as Ends,
  endOn: new Date(Date.now() + 365 * 24 * 3600 * 1000),
  weekDays: ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'] as const,
  selectedWeekDays: ['Tue'] as string[],
  monthDay: [1],
  customSelectedDates: [] as Date[],
  customMonths: [] as number[],
  cronExpr: props.cronExpression ?? '',
  selectedClientIds: [] as number[],
})

const weekDaysData = [
  { value: 'Mon', label: t('dateTime.weekdays.monday') },
  { value: 'Tue', label: t('dateTime.weekdays.tuesday') },
  { value: 'Wed', label: t('dateTime.weekdays.wednesday') },
  { value: 'Thu', label: t('dateTime.weekdays.thursday') },
  { value: 'Fri', label: t('dateTime.weekdays.friday') },
  { value: 'Sat', label: t('dateTime.weekdays.saturday') },
  { value: 'Sun', label: t('dateTime.weekdays.sunday') }
]

const showWeekdayPicker = computed(
  () => state.mode === 'interval' && state.unit === 'Week'
)

function toggleWeekday(day: string) {
  const i = state.selectedWeekDays.indexOf(day)
  if (i >= 0) state.selectedWeekDays.splice(i, 1)
  else state.selectedWeekDays.push(day)
}

function toggleMonthDay(day: number) {
  const idx = state.monthDay.indexOf(day)
  if (idx >= 0) state.monthDay.splice(idx, 1)
  else state.monthDay.push(day)
}

const customFromDates = computed(() => {
  const month = new Set<number>()
  const day = new Set<number>()
  for (const date of state.customSelectedDates) {
    const obj = new Date(date)
    month.add(obj.getMonth() + 1)
    day.add(obj.getDate())
  }
  return { months: [...month], days: [...day] }
})

watch(customFromDates, ({ months, days }) => {
  state.customMonths = months
  state.monthDay = days
})

const showGroupSelector = computed(() => state.actionType === Type.moveToGroup)
const showMessageField = computed(() => state.actionType === Type.sendMessage)
const showDelegateSelector = computed(() => state.actionType === Type.wakeUp)

const availableDelegateClients = computed(() => {
  const targetIds: number[] = typeof props.clientId === 'number' ? [props.clientId] : []
  // Only include clients that are connected (active) and not the target client
  return clientsStore.clients.filter(
    client => typeof client.id === 'number' && client.connected === true && !targetIds.includes(client.id)
  )
})

const intervalUnits = computed(() => [
  { value: 'Minute', label: t('scheduler.units.minutes') },
  { value: 'Hour', label: t('scheduler.units.hours') },
  { value: 'Day', label: t('scheduler.units.days') },
  { value: 'Week', label: t('scheduler.units.weeks') },
  { value: 'Month', label: t('scheduler.units.months') },
  { value: 'Custom', label: t('scheduler.units.custom') }
])

function hydrateWeekdaysFromCron(expr: string) {
  const parts = expr.split(/\s+/)
  if (parts.length < 5) return
  const dow = parts[4]
  if (dow === '*') return

  const map: Record<string, string> = {
    '0': 'Sun', '7': 'Sun',
    '1': 'Mon', '2': 'Tue', '3': 'Wed', '4': 'Thu', '5': 'Fri', '6': 'Sat',
    MON: 'Mon', TUE: 'Tue', WED: 'Wed', THU: 'Thu', FRI: 'Fri', SAT: 'Sat', SUN: 'Sun'
  }

  const out: string[] = []
  const days = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun']

  dow.split(',').forEach(token => {
    const t = token.toUpperCase().trim()

    if (t.includes('-')) {
      const [start, end] = t.split('-')
      const s = map[start], e = map[end]
      if (s && e) {
        const si = days.indexOf(s)
        const ei = days.indexOf(e)
        for (let i = si; i <= ei; i++) out.push(days[i])
      }
      return
    }

    if (map[t]) out.push(map[t])
  })

  if (out.length) {
    state.mode = 'interval'
    state.unit = 'Week'
    state.selectedWeekDays = out
  }
}

function hydrateFromProps() {
  if (!props.actionId) return

  state.actionType = props.actionType ?? state.actionType
  state.description = props.description ?? ''
  // parse extraData safely (handles object or JSON string)
  state.extraData = parseExtraData(props.extraData)

  // ensure message field exists as a string when needed
  if (state.actionType === Type.sendMessage && state.extraData.message == null) {
    state.extraData.message = ''
  }

  state.timezone = props.timezone ?? userTimezone.value
  state.cronExpr = props.cronExpression ?? ''

  if (props.startAt) {
    const date = new Date(props.startAt)
    state.startDateTime = date
    state.onceDateTime = date
  }
  if (props.endAt) {
    state.ends = 'on'
    state.endOn = new Date(props.endAt)
  } else {
    state.ends = 'never'
  }

  // use parsed object from state.extraData and guard its type
  const parsedExtra = state.extraData as Record<string, any>
  if (Array.isArray(parsedExtra.selectedWeekDays) && parsedExtra.selectedWeekDays.length) {
    state.mode = 'interval'
    state.unit = 'Week'
    state.selectedWeekDays = [...parsedExtra.selectedWeekDays]
  } else if (props.cronExpression) {
    hydrateWeekdaysFromCron(props.cronExpression)
  }
}

watch(() => props.isOpen, open => {
  if (open && props.actionId) hydrateFromProps()

  if (open && state.actionType === Type.sendMessage && state.extraData?.message == null) {
    state.extraData = { ...(state.extraData ?? {}), message: '' }
  }
})


watch(() => props.isOpen, open => {
  if (open && props.actionId) hydrateFromProps()

  if (open && state.actionType === Type.sendMessage && state.extraData?.message == null) {
    state.extraData = { ...(state.extraData ?? {}), message: '' }
  }
})

function formatDateToUTC(date: Date, timezone: string) {
  const targetDate = new Date(date)

  // Use Intl to format with timezone
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  })

  // Get parts and construct a new Date in UTC
  const parts = formatter.formatToParts(targetDate)
    .reduce((acc, part) => {
      if (part.type !== 'literal') {
        acc[part.type] = part.value
      }
      return acc
    }, {} as Record<string, string>)

  // Create ISO string with timezone
  return new Date(
    `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`
  ).toISOString()
}

async function save() {
  try {
    if (state.actionType === Type.wakeUp && !state.delegateClientId) {
      toast.add({
        severity: 'error',
        summary: t('common.toast.error'),
        detail: t('scheduler.delegateClientRequired'),
        life: 4000
      })
      return
    }

    const cronExpression = generateCronExpression(state)
    const startAt =
      state.mode === 'once'
        ? formatDateToUTC(state.onceDateTime, state.timezone)
        : formatDateToUTC(state.startDateTime, state.timezone)

    const endAt = state.ends === 'on'
      ? formatDateToUTC(state.endOn, state.timezone)
      : undefined

    const extraDataSafe = { ...(state.extraData ?? {}) }
    if (state.actionType === Type.sendMessage) {
      extraDataSafe.message = String(extraDataSafe.message ?? '')
    }
    // Add MAC addresses for wakeUp action
    if (state.actionType === Type.wakeUp) {
      let macs: string[] = []
      if (props.clientId) {
        const client = clientsStore.clients.find(c => c.id === props.clientId)
        if (client && Array.isArray(client.networkInterfaces)) {
          macs = client.networkInterfaces
            .map(ni => ni.macAddress)
            .filter(Boolean) as string[]
        }
      } else if (Array.isArray(state.selectedClientIds) && state.selectedClientIds.length) {
        // Collect all MAC addresses from all selected clients
        const validClientIds = state.selectedClientIds.filter((id): id is number => true)
        macs = validClientIds.flatMap(id => {
          const client = clientsStore.clients.find(c => c.id === id)
          if (client && Array.isArray(client.networkInterfaces)) {
            return client.networkInterfaces
              .map(ni => ni.macAddress)
              .filter(Boolean)
          }
          return []
        })
      } else if (props.groupId) {
        // Collect all clients in the group and their MACs
        const groupClients = clientsStore.clients.filter(c => c.clientGroupId === props.groupId)
        macs = groupClients.flatMap(client =>
          Array.isArray(client.networkInterfaces)
            ? client.networkInterfaces.map(ni => ni.macAddress).filter(Boolean)
            : []
        )
      }
      console.log('Wakeup action - selected MAC addresses:', macs)
      extraDataSafe.macs = macs
    }

    const extraDataString = JSON.stringify(extraDataSafe)

    let clientIdsForDto: number[] | undefined = undefined;
    if (state.actionType === Type.wakeUp) {
      if (Array.isArray(state.selectedClientIds) && state.selectedClientIds.length > 0) {
        clientIdsForDto = state.selectedClientIds;
      } else if (props.clientId) {
        clientIdsForDto = [props.clientId];
      } else if (props.clientIds && props.clientIds.length > 0) {
        clientIdsForDto = props.clientIds;
      }
    } else {
      if (props.clientIds && props.clientIds.length > 0) {
        clientIdsForDto = props.clientIds;
      } else if (props.clientId) {
        clientIdsForDto = [props.clientId];
      }
    }

    const dto = {
      type: state.actionType,
      description: state.description,
      startAt,
      endAt,
      cronExpression,
      timezone: state.timezone,
      extraData: extraDataString,
      clientIds: clientIdsForDto,
      groupIds: props.groupId ? [props.groupId] : undefined,
      delegateClientId: state.delegateClientId,
    }

    if (state.actionType === Type.wakeUp) {
      console.debug('[DEBUG] Scheduling wakeup action with DTO:', dto)
    }

    let response = null

    if (props.actionId) {
      const updateDto = {
        type: state.actionType,
        description: dto.description,
        startAt: dto.startAt,
        endAt: dto.endAt,
        cronExpression: dto.cronExpression,
        timezone: dto.timezone,
        extraData: extraDataString,
        delegateClientId: state.delegateClientId,
      }
      response = await scheduledActionsStore.updateAction(updateDto, props.actionId)
    } else if (clientIdsForDto && clientIdsForDto.length > 0) {
      response = await scheduledActionsStore.createClientScheduledActions({ ...dto, clientIds: clientIdsForDto })
    } else if (props.groupId) {
      response = await scheduledActionsStore.createGroupScheduledActions(dto)
    }

    toast.add({
      severity: 'success',
      summary: t('common.toast.success'),
      detail: props.actionId
        ? t('scheduler.updateSuccess')
        : t('scheduler.createSuccess'),
      life: 3000
    })

    emit('save', dto)
    emit('saved', response)
    emit('update:isOpen', false)
  } catch (err: any) {
    toast.add({
      severity: 'error',
      summary: t('common.toast.error'),
      detail: err?.message ?? String(err),
      life: 4000
    })
  }
}

const actionTypeOptions = computed(() =>
  Object.values(Type).map(v => ({ label: cleanText(v), value: v }))
)

const generatedCron = computed(() => generateCronExpression(state))
</script>


<template>
  <Dialog
    v-model:visible="visible"
    modal
    :header="t('scheduler.title')"
    :closable="true"
    :dismissableMask="true"
    @hide="close"
    :style="{ width: '55rem' }"
  >
    <form class="form-grid">
      <!-- Schedule Type at the top -->
      <template v-if="!props.actionId">
        <div class="label-cell">
        <label class="required">{{ t('scheduler.schedulerType.scheduleType') }}</label>
      </div>
        <div class="field-cell">
        <div class="flex gap-4">
          <div class="flex items-center">
            <RadioButton value="once" v-model="state.mode" inputId="mode-once" />
            <label for="mode-once" class="ml-2">{{ t('scheduler.schedulerType.once') }}</label>
          </div>
          <div class="flex items-center">
            <RadioButton value="interval" v-model="state.mode" inputId="mode-interval" />
            <label for="mode-interval" class="ml-2">{{ t('scheduler.schedulerType.recurring') }}</label>
          </div>
          <div class="flex items-center">
            <RadioButton value="cron" v-model="state.mode" inputId="mode-cron" />
            <label for="mode-cron" class="ml-2">{{ t('scheduler.schedulerType.cron') }}</label>
          </div>
        </div>
      </div>
      </template>

      <!-- Action Type -->
      <div class="label-cell pt-4">
        <label class="required">{{ t('scheduler.actionType') }}</label>
      </div>
      <div class="field-cell">
        <Dropdown
          v-model="state.actionType"
          :options="actionTypeOptions"
          optionLabel="label"
          optionValue="value"
          class="w-full"
        />
      </div>

      <!-- Multi-client selection for wakeUp -->
      <template v-if="state.actionType === Type.wakeUp">
        <div class="label-cell pt-4">
          <label class="required">{{ t('scheduler.selectClients') }}</label>
        </div>
        <div class="field-cell">
          <MultiSelect
            v-model="state.selectedClientIds"
            :options="clientsStore.clients"
            optionLabel="hostname"
            optionValue="id"
            multiple
            filter
            placeholder="Select clients to wake up"
            class="w-full"
          />
        </div>
      </template>

      <!-- Delegate Client Selection (only for WoL actions) -->
      <template v-if="showDelegateSelector">
        <div class="label-cell pt-4">
          <label>{{ t('scheduler.delegateClient') }}</label>
        </div>
        <div class="field-cell">
          <Dropdown
            v-model="state.delegateClientId"
            :options="availableDelegateClients"
            optionLabel="hostname"
            optionValue="id"
            :placeholder="t('scheduler.delegateClientPlaceholder')"
            class="w-full"
            :clearable="true"
            filter
          />
          <small class="text-gray-500 block mt-1">
            {{ t('scheduler.delegateClientHelp') }}
          </small>
        </div>
      </template>

      <!-- Target Group Selection (only for moveToGroup action type) -->
      <template v-if="showGroupSelector">
        <div class="label-cell pt-4">
          <label class="required">{{ t('scheduler.targetGroup') }}</label>
        </div>
        <div class="field-cell">
          <Dropdown
            v-model="state.extraData.targetGroupId"
            :options="availableGroups"
            optionLabel="label"
            optionValue="value"
            :loading="isLoadingGroups"
            class="w-full"
            :placeholder="t('scheduler.selectTargetGroup')"
          />
        </div>
      </template>

      <template v-if="showMessageField">
        <div class="label-cell pt-4">
          <label class="required">{{ t('scheduler.message') }}</label>
        </div>
        <div class="field-cell">
          <InputText
            v-model="state.extraData.message"
            class="w-full"
            :placeholder="t('scheduler.messagePlaceholder')"
          />
        </div>
      </template>

      <!-- Description -->
      <div class="label-cell pt-4">
        <label>{{ t('scheduler.description') }}</label>
      </div>
      <div class="field-cell">
        <InputText v-model="state.description" class="w-full" />
      </div>

      <!-- Timezone -->
      <div class="label-cell pt-4">
        <label>{{ t('scheduler.timezone') }}</label>
      </div>
      <div class="field-cell">
        <Dropdown v-model="state.timezone" :options="timezones" optionLabel="label"
                  optionValue="value" class="w-full" />
      </div>

      <!-- ONCE -->
      <template v-if="state.mode === 'once'">
        <div class="label-cell pt-4">
          <label class="required">{{ t('scheduler.executionDateTime') }}</label>
        </div>
        <div class="field-cell">
          <Calendar
            v-model="state.onceDateTime"
            showTime
            hourFormat="24"
            :showIcon="true"
            class="w-full"
            update-model-type="date"
            fluid
          />
        </div>
      </template>

      <!-- INTERVAL -->
      <template v-if="state.mode === 'interval'">
        <div class="label-cell pt-5">
          <label class="required">{{ t('scheduler.repeatEvery') }}</label>
        </div>
        <div class="field-cell">
          <div class="flex items-center gap-2">
            <InputNumber
              v-model="state.every"
              :min="1"
              :max="100"
              showButtons
              buttonLayout="horizontal"
              class="narrow-input"
              :disabled="state.unit === 'Custom'"
            />
            <Dropdown
              v-model="state.unit"
              :options="intervalUnits"
              optionLabel="label"
              optionValue="value"
              class="w-9rem"
            />
          </div>
        </div>

        <template v-if="showWeekdayPicker">
          <div class="label-cell">
            <label class="required">{{ t('scheduler.executeEveryWeekOn') }}</label>
          </div>
          <div class="field-cell">
            <div class="flex flex-wrap gap-1">
              <div
                v-for="day in weekDaysData"
                :key="day.value"
                class="cursor-pointer p-2 border rounded-md"
                :class="state.selectedWeekDays.includes(day.value) ? 'bg-primary text-white' : 'hsl(var(--card))'"
                @click="toggleWeekday(day.value)"
              >
                {{ day.label }}
              </div>
            </div>
          </div>
        </template>

        <template v-else-if="state.unit === 'Month'">
          <div class="label-cell">
            <label class="required">{{ t('scheduler.executeEveryMonthOn') }}</label>
          </div>
          <div class="field-cell">
            <div class="month-day-picker">
              <div
                v-for="day in 31"
                :key="day"
                class="month-day-cell"
                :class="{ selected: state.monthDay.includes(day) }"
                @click="toggleMonthDay(day)"
              >
                {{ day }}
              </div>
            </div>
            <small class="text-gray-500 block mt-1">
              {{ t('scheduler.multiDayHint') }}
            </small>
          </div>
        </template>

        <!-- CUSTOM -->
        <template v-else-if="state.unit === 'Custom'">
          <div class="label-cell">
            <label class="required">{{ t('scheduler.pickDates') }}</label>
          </div>
          <div class="field-cell">
            <Calendar
              v-model="state.customSelectedDates"
              selectionMode="multiple"
              :showIcon="true"
              :numberOfMonths="2"
              :manualInput="false"
              panelClass="compact-calendar"
              fluid
              update-model-type="date"
            />
            <small class="text-gray-500 block mt-2">
              {{ t('scheduler.customHint') }}
            </small>
          </div>
        </template>


        <div class="label-cell pt-4">
          <label class="required">{{ t('scheduler.startDateTime') }}</label>
        </div>
        <div class="field-cell">
          <Calendar v-model="state.startDateTime" showTime hourFormat="24" :showIcon="true" panelClass="compact-calendar"
                    class="w-full" update-model-type="date" fluid/>
        </div>

        <div v-if="state.unit!='Custom'" class="label-cell pt-4">
          <label>{{ t('scheduler.endSchedule') }}</label>
        </div>
        <div v-if="state.unit!='Custom'" class="field-cell">
          <div class="flex items-center mb-2">
            <RadioButton value="never" v-model="state.ends" inputId="e-never" />
            <label for="e-never" class="ml-2">{{ t('scheduler.never') }}</label>
          </div>
          <div class="flex items-center gap-2">
            <RadioButton class="mt-2" value="on" v-model="state.ends" inputId="e-on" />
            <label for="e-on" class="mr-2 mt-2">{{ t('scheduler.on') }}</label>
            <Calendar
              v-model="state.endOn"
              showTime
              hourFormat="24"
              panelClass="compact-calendar"
              :disabled="state.ends !== 'on'"
              :showIcon="true"
              update-model-type="date"
              fluid
            />
          </div>
        </div>
      </template>

      <!-- CRON -->
      <template v-if="state.mode === 'cron'">
        <div class="label-cell pt-4">
          <label class="required">{{ t('scheduler.cronExpression') }}</label>
        </div>
        <div class="field-cell">
          <InputText v-model="state.cronExpr" class="w-full" placeholder="0 0 * * *" />
        </div>
      </template>

      <!-- Debug display -->
      <template v-if="state.mode !== 'cron'">
        <div class="label-cell">
          <label>{{ t('scheduler.generatedCronExpression') }}</label>
        </div>
        <div class="field-cell">
          <div class="px-2 py-1 rounded">{{ generatedCron }}</div>
        </div>
      </template>
    </form>

    <template #footer>
      <div class="popup-footer">
        <Button :label="t('common.actions.save')" @click="save" class="button submit" />
        <Button :label="t('common.actions.cancel')" @click="close" class="button cancel" />
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
.form-grid {
  display: grid;
  grid-template-columns: 1fr 4fr;
  gap: 0.75rem;
  width: 100%;
}

.label-cell {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  padding-top: 0.5rem;
  padding-left: 0.75rem;
  margin-right: 4rem;
  min-width: 15rem;
}

.field-cell {
  padding: 0.25rem 0;
}

label.required:after {
  content: "*";
  color: red;
  margin-left: 0.25rem;
}

.button {
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  margin-left: 10px;
}
.cancel { background-color: #fbc02d; }
.submit { background-color: #689f38; color: #fff; }

/* Make the overall calendar popup smaller */
:deep(.p-datepicker) {
  font-size: 0.8rem; /* shrink text */
  width: 16rem !important; /* control overall width */
  box-shadow: none !important; /* remove shadow for cleaner look */
}

/* Shrink the calendar cells */
:deep(.p-datepicker table td) {
  padding: 0.25rem !important; /* smaller cells */
  font-size: 0.75rem;          /* smaller numbers */
}

/* Shrink header controls (month/year selector, arrows) */
:deep(.p-datepicker .p-datepicker-header) {
  padding: 0.25rem !important;
  font-size: 0.8rem;
}

:deep(.p-timepicker) {
  font-size: 0.75rem;
}

:deep(.p-timepicker button) {
  width: 1.5rem;
  height: 1.5rem;
}

:deep(.narrow-input) {
  width: auto !important;
}

:deep(.narrow-input .p-inputtext) {
  width: 4rem !important; /* controls the actual field width */
  text-align: center;
}

:deep(.narrow-input .p-inputnumber-button) {
  padding: 0.25rem !important; /* make buttons smaller */
}

.month-day-picker {
  display: grid;
  grid-template-columns: repeat(7, 2.2rem);
  gap: 0.4rem;
  user-select: none;
}

.month-day-cell {
  border: 1px solid hsl(var(--border));
  border-radius: 6px;
  text-align: center;
  padding: 0.3rem 0;
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: 0.85rem;
  background-color: var(--card);
  color: hsl(var(--foreground) / 0.5);
}

.month-day-cell:hover {
  background-color: hsl(var(--accent));
}

.month-day-cell.selected {
  background-color: hsl(var(--accent));
  color: var(--foreground);
  font-weight: 600;
  border-color: hsl(var(--accent));
}
</style>
